import { useCallback, useEffect, useRef, useState, type SetStateAction } from 'react';
import { api, ApiError } from './api.ts';
import { useAuth } from './auth.tsx';

/** Hämtar data från API:t och laddar om när adressen ändras. */
export function useApi<T>(url: string | null) {
  const [state, setState] = useState<{
    url: string | null;
    data: T | null;
    error: ApiError | null;
    loading: boolean;
  }>({ url, data: null, error: null, loading: !!url });
  const current = useRef(url);
  const request = useRef(0);
  current.current = url;

  const load = useCallback(async () => {
    // Ett avslutat anrop från en tidigare sida kan fortfarande hålla den gamla reload-funktionen.
    // Det får varken starta en hämtning eller ogiltigförklara den nya sidans pågående GET.
    if (!url || current.current !== url) return;
    const ownRequest = ++request.current;
    setState((previous) => ({
      url,
      data: previous.url === url ? previous.data : null,
      error: null,
      loading: true,
    }));
    try {
      const result = await api.get<T>(url);
      if (current.current === url && request.current === ownRequest) {
        setState({ url, data: result, error: null, loading: false });
      }
    } catch (e) {
      if (current.current === url && request.current === ownRequest) {
        setState((previous) => ({
          url,
          data: previous.url === url ? previous.data : null,
          error: e instanceof ApiError ? e : new ApiError(0, String(e)),
          loading: false,
        }));
      }
    }
  }, [url]);

  useEffect(() => {
    void load();
  }, [load]);

  const setData = useCallback(
    (value: SetStateAction<T | null>) => {
      setState((previous) =>
        previous.url === url
          ? {
              ...previous,
              data:
                typeof value === 'function' ? (value as (data: T | null) => T | null)(previous.data) : value,
            }
          : previous,
      );
    },
    [url],
  );
  return {
    data: state.url === url ? state.data : null,
    error: state.url === url ? state.error : null,
    loading: !!url && (state.url !== url || state.loading),
    reload: load,
    setData,
  };
}

/** Kör en åtgärd (t.ex. att skicka ett formulär) och håller reda på fel och väntetid. */
export function useAction<A extends unknown[], R>(fn: (...args: A) => Promise<R>) {
  const [pending, setPending] = useState(false);
  const inFlight = useRef(false);
  const [error, setError] = useState<ApiError | null>(null);
  const run = useCallback(
    async (...args: A): Promise<R | undefined> => {
      if (inFlight.current) return undefined;
      inFlight.current = true;
      setPending(true);
      setError(null);
      try {
        return await fn(...args);
      } catch (e) {
        setError(e instanceof ApiError ? e : new ApiError(0, String(e)));
        return undefined;
      } finally {
        inFlight.current = false;
        setPending(false);
      }
    },
    [fn],
  );
  return { run, pending, error, setError };
}

export function useTitle(title: string) {
  useEffect(() => {
    document.title = title ? `${title} – Aurora` : 'Aurora';
  }, [title]);
}

export type AutosaveStatus = 'idle' | 'saving' | 'saved' | 'error' | 'conflict';

interface PendingSave {
  value: string;
  payload: unknown;
}

interface SaveQueue {
  key: string;
  url: string;
  pending: PendingSave | null;
  inFlight: Promise<void> | null;
  suspended: number;
  keepalive: boolean;
  status: AutosaveStatus;
  listeners: Set<(status: AutosaveStatus) => void>;
  clientId: string;
  sequence: number;
  ownerId: number;
  persisted: PendingSave | null;
}

// Samma kö används även om deltagaren lämnar delen och genast öppnar den igen.
// Bara ett PUT per anteckning/utkast får pågå, så ett gammalt svar kan inte skriva över ett nytt.
const saveQueues = new Map<string, SaveQueue>();

function persistPending(queue: SaveQueue) {
  try {
    if (queue.pending) {
      localStorage.setItem(queue.key, JSON.stringify(queue.pending));
      queue.persisted = queue.pending;
    } else if (queue.persisted && localStorage.getItem(queue.key) === JSON.stringify(queue.persisted)) {
      // En annan flik kan ha sparat en ny lokal kopia medan vårt PUT väntade på svar.
      localStorage.removeItem(queue.key);
      queue.persisted = null;
    }
  } catch {
    // Webbläsaren kan neka lokal lagring. Den pågående sidans kö finns ändå kvar i minnet.
  }
}

function setSaveStatus(queue: SaveQueue, status: AutosaveStatus) {
  queue.status = status;
  for (const notify of queue.listeners) notify(status);
}

function saveQueue(key: string, url: string, ownerId: number): SaveQueue {
  const existing = saveQueues.get(key);
  if (existing) return existing;
  let pending: PendingSave | null = null;
  try {
    const stored = JSON.parse(localStorage.getItem(key) ?? 'null');
    if (stored && typeof stored.value === 'string' && stored.payload && typeof stored.payload === 'object') {
      pending = stored;
    }
  } catch {
    // En skadad eller otillgänglig lokal kopia påverkar inte serverns sparade text.
  }
  let clientId: string = crypto.randomUUID();
  let sequence = 0;
  try {
    const stream = JSON.parse(sessionStorage.getItem(`${key}:stream`) ?? 'null');
    if (stream && typeof stream.clientId === 'string' && Number.isSafeInteger(stream.sequence)) {
      clientId = stream.clientId;
      sequence = stream.sequence;
    }
    const saved = (pending?.payload as { autosave?: { clientId: string; sequence: number } } | undefined)
      ?.autosave;
    if (saved && typeof saved.clientId === 'string' && Number.isSafeInteger(saved.sequence)) {
      // Återspelningen behåller sin metadata, men nya redigeringar hör till den här flikens stream.
      if (saved.clientId === clientId) sequence = Math.max(sequence, saved.sequence);
    }
  } catch {
    // Utan sessionStorage fungerar fortfarande ordningen under den öppna sidans livstid.
  }
  const queue: SaveQueue = {
    key,
    url,
    pending,
    inFlight: null,
    suspended: 0,
    keepalive: false,
    status: pending ? 'saving' : 'idle',
    listeners: new Set(),
    clientId,
    sequence,
    ownerId,
    persisted: pending,
  };
  saveQueues.set(key, queue);
  return queue;
}

function flushSave(queue: SaveQueue): Promise<void> {
  if (queue.inFlight) return queue.inFlight;
  if (queue.suspended || !queue.pending || !queue.ownerId) return Promise.resolve();
  const run = async () => {
    while (queue.pending && !queue.suspended) {
      const sent = queue.pending;
      const savedPayload = sent.payload as { autosave?: Record<string, unknown> };
      const payload = { ...savedPayload, autosave: { ...savedPayload.autosave, ownerId: queue.ownerId } };
      setSaveStatus(queue, 'saving');
      try {
        let result: { applied?: boolean; reason?: string };
        if (queue.keepalive) {
          const response = await fetch(queue.url, {
            method: 'PUT',
            keepalive: true,
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });
          if (!response.ok) throw new Error('Sparningen misslyckades.');
          result = await response.json();
        } else {
          result = await api.put(queue.url, payload);
        }
        if (result.applied === false && result.reason === 'wrong_owner') {
          setSaveStatus(queue, 'error');
          return;
        }
        if (result.applied === false) {
          // Ett ignorerat anrop är inte en kvittens på texten. Bevara den för uttryckligt återförsök.
          if (queue.pending === sent) {
            setSaveStatus(queue, 'conflict');
            return;
          }
        }
      } catch {
        // Behåll senaste texten för nästa försök, även efter navigation eller stängd flik.
        setSaveStatus(queue, 'error');
        return;
      }
      if (queue.pending === sent && !queue.suspended) {
        queue.pending = null;
        persistPending(queue);
        setSaveStatus(queue, 'saved');
      }
    }
  };
  const saving = run().finally(() => {
    if (queue.inFlight === saving) queue.inFlight = null;
    if (!queue.pending && !queue.listeners.size) saveQueues.delete(queue.key);
  });
  queue.inFlight = saving;
  return saving;
}

/**
 * Sparar text automatiskt en stund efter att användaren slutat skriva. Text som inte hunnit
 * sparas skickas ändå när komponenten försvinner (byte av del) eller när fliken stängs.
 * En kontospecifik lokal kopia behålls tills servern kvitterat senaste texten.
 * value är null tills användaren har ändrat något.
 */
export function useAutosave(
  url: string,
  value: string | null,
  delay: number,
  toBody: (value: string) => Record<string, unknown>,
): {
  status: AutosaveStatus;
  recovered: string | null;
  retry: () => void;
  commit: <T>(send: () => Promise<T>) => Promise<T>;
} {
  const { user } = useAuth();
  const key = `aurora:autosave:${user?.id ?? 'anonymous'}:${url}`;
  const queue = saveQueue(key, url, user?.id ?? 0);
  const [status, setStatus] = useState<AutosaveStatus>(queue.status);
  const [recovered, setRecovered] = useState<string | null>(queue.pending?.value ?? null);
  const body = useRef(toBody);
  body.current = toBody;

  useEffect(() => {
    if (value === null) return;
    queue.keepalive = false;
    queue.sequence++;
    queue.pending = {
      value,
      payload: {
        ...body.current(value),
        autosave: { clientId: queue.clientId, sequence: queue.sequence, ownerId: queue.ownerId },
      },
    };
    try {
      sessionStorage.setItem(
        `${queue.key}:stream`,
        JSON.stringify({ clientId: queue.clientId, sequence: queue.sequence }),
      );
    } catch {
      // Den lokala textkopian och kön i minnet behålls även om sessionStorage inte fungerar.
    }
    persistPending(queue);
    setSaveStatus(queue, 'saving');
    const t = window.setTimeout(() => void flushSave(queue), delay);
    return () => window.clearTimeout(t);
  }, [value, queue, delay]);

  useEffect(() => {
    queue.listeners.add(setStatus);
    setStatus(queue.status);
    setRecovered(queue.pending?.value ?? null);
    // Återskapa texten efter en misslyckad sparning eller stängd flik och försök igen.
    if (queue.pending) void flushSave(queue);
    const retry = () => void flushSave(queue);
    const onShow = () => {
      queue.keepalive = false;
      if (queue.pending) void flushSave(queue);
    };
    const onHide = () => {
      queue.keepalive = true;
      // Starta aldrig ett parallellt PUT här: kön fortsätter efter det pågående anropet.
      // Om dokumentet stängs helt finns den senaste texten kvar för nästa öppning.
      persistPending(queue);
      void flushSave(queue);
    };
    window.addEventListener('pagehide', onHide);
    window.addEventListener('pageshow', onShow);
    window.addEventListener('online', retry);
    return () => {
      window.removeEventListener('pagehide', onHide);
      window.removeEventListener('pageshow', onShow);
      window.removeEventListener('online', retry);
      queue.listeners.delete(setStatus);
      void flushSave(queue);
    };
  }, [queue]);

  // T.ex. när inlämningen skickats: utkastet ska inte sparas på nytt efteråt.
  const commit = useCallback(
    async <T>(send: () => Promise<T>): Promise<T> => {
      // Ett äldre utkast måste bli färdigt före inlämningen, annars kan det återskapas efter POST.
      queue.suspended++;
      try {
        await queue.inFlight;
        const result = await send();
        queue.pending = null;
        persistPending(queue);
        setRecovered(null);
        setSaveStatus(queue, 'idle');
        return result;
      } finally {
        queue.suspended--;
        if (queue.pending) void flushSave(queue);
      }
    },
    [queue],
  );
  const retry = () => {
    queue.keepalive = false;
    if (queue.pending && queue.status === 'conflict') {
      // En klonad flik kan ha samma gamla sessionStorage. Ett uttryckligt återförsök får en egen stream.
      queue.clientId = crypto.randomUUID();
      queue.sequence = 1;
      queue.pending = {
        value: queue.pending.value,
        payload: {
          ...body.current(queue.pending.value),
          autosave: { clientId: queue.clientId, sequence: queue.sequence, ownerId: queue.ownerId },
        },
      };
      try {
        sessionStorage.setItem(
          `${queue.key}:stream`,
          JSON.stringify({ clientId: queue.clientId, sequence: queue.sequence }),
        );
      } catch {}
      persistPending(queue);
    }
    void flushSave(queue);
  };
  return { status, recovered, retry, commit };
}
